import React from "react";
import { Box, Button, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { STATUS_STYLES } from "./statusStyles";

export const TRACK_MIN_WIDTH = 560;
export const ROW_GRID = "minmax(180px, 240px) minmax(560px, 1fr) auto";

function monthYear(date) {
  return date ? date.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "";
}

/** Right-hand status line: "Expired 197 days ago", "23 days remaining", "14 months remaining". */
export function remaining(row) {
  if (row.kind === "missing") return "Not on file";
  if (row.daysLeft === null) return "No expiry";
  if (row.daysLeft < 0) return `Expired ${-row.daysLeft} day${row.daysLeft === -1 ? "" : "s"} ago`;
  if (row.daysLeft === 0) return "Expires today";
  if (row.daysLeft <= 60) return `${row.daysLeft} day${row.daysLeft === 1 ? "" : "s"} remaining`;
  const months = Math.round(row.daysLeft / 30);
  return `${months} month${months === 1 ? "" : "s"} remaining`;
}

const actionSx = { textTransform: "none", fontWeight: 600, fontSize: 12.5, borderRadius: "8px", whiteSpace: "nowrap" };

function TodayLine({ left }) {
  return <Box sx={{ position: "absolute", top: 0, bottom: 0, left: `${left}%`, width: "2px", bgcolor: "text.primary", opacity: 0.35 }} />;
}

/** One certification (or one missing requirement) on the validity timeline. */
export default function TimelineRow({ row, axis, dim = false, onDownload, onShare, onRenew, onUpload }) {
  const style = STATUS_STYLES[row.status];
  const isMissing = row.kind === "missing";
  const canRenew = row.status === "expired" || row.status === "expiring";
  return (
    <Box
      role="row"
      data-testid={isMissing ? "cert-row-missing" : "cert-row"}
      data-status={row.status}
      data-dim={dim ? "true" : undefined}
      sx={{ display: "grid", gridTemplateColumns: ROW_GRID, alignItems: "center", gap: 2, py: 1.5, borderTop: "1px solid var(--color-hairline-cloud, #e5e7eb)", opacity: dim ? 0.35 : 1, transition: "opacity .15s" }}
    >
      <Box role="cell" sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{row.name}</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{[row.issuer, row.certNumber, row.issued && `Issued ${monthYear(row.issued)}`].filter(Boolean).join(" · ")}</Typography>
      </Box>

      <Box role="cell" aria-hidden="true" sx={{ position: "relative", height: 28 }}>
        <Box sx={{ position: "absolute", inset: "10px 0", bgcolor: "var(--color-surface-2, #f4f4f6)", borderRadius: 99 }} />
        {isMissing ? (
          <Box sx={{ position: "absolute", inset: "4px 0", border: "1.5px dashed", borderColor: style.bar, borderRadius: 99, display: "grid", placeItems: "center" }}>
            <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: style.color, bgcolor: "background.paper", px: 1 }}>Missing required certification</Typography>
          </Box>
        ) : row.bar && (
          <Box data-testid="cert-bar" sx={{ position: "absolute", top: 2, height: 24, left: `${row.bar.left}%`, width: `${row.bar.width}%`, minWidth: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 1, overflow: "hidden", whiteSpace: "nowrap", borderRadius: "6px", fontFamily: "var(--font-mono, monospace)", fontSize: 11, fontWeight: 600, ...(row.status === "expired" ? { bgcolor: "var(--color-danger-bg)", color: "var(--color-danger)", border: "1px solid rgba(180,35,24,.3)" } : { bgcolor: style.bar, color: "#fff" }) }}>
            <span>{row.certNumber}</span><span>{monthYear(row.expiresAt)}</span>
          </Box>
        )}
        <TodayLine left={axis.todayPct} />
      </Box>

      <Box role="cell" sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
        <Typography data-testid="cert-status-text" sx={{ fontSize: 12, fontWeight: 600, color: style.color, mr: "auto", pr: 1 }}>{remaining(row)}</Typography>
        {isMissing ? (
          <Button size="small" data-testid="cert-action-upload" aria-label={`Upload ${row.name}`} startIcon={<UploadFileIcon />} onClick={() => onUpload(row)} sx={actionSx}>Upload</Button>
        ) : (
          <>
            {row.hasDocument && (
              <Button size="small" data-testid="cert-action-download" aria-label={`Download PDF for ${row.name}`} startIcon={<DownloadIcon />} onClick={() => onDownload(row)} sx={actionSx}>Download PDF</Button>
            )}
            {row.shareUrl && (
              <Button size="small" data-testid="cert-action-share" aria-label={`Copy verified link for ${row.name}`} startIcon={<ShareIcon />} onClick={() => onShare(row)} sx={actionSx}>Share</Button>
            )}
            {canRenew && (
              <Button size="small" variant="contained" disableElevation data-testid="cert-action-renew" aria-label={`Renew ${row.name} now`} startIcon={<RefreshIcon />} onClick={() => onRenew(row)} sx={{ ...actionSx, backgroundImage: "none" }}>Renew now</Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
